/**
 * Chart Data Transformer Utility
 *
 * Transforms various data formats from the backend into the standardized
 * format expected by the DataChart component.
 *
 * Expected Output Format:
 * {
 *   day: { data: number[], labels: string[] },
 *   month: { data: number[], labels: string[] },
 *   year: { data: number[], labels: string[] }
 * }
 */

export interface ChartDataPoint {
	data: number[];
	labels: string[];
}

export interface ChartData {
	day?: ChartDataPoint;
	month?: ChartDataPoint;
	year?: ChartDataPoint;
}

/**
 * Transform backend stats data to chart format
 * @param statsStructure - Raw data from backend
 * @returns Transformed data in chart format or null if invalid
 */
export const transformStatsForChart = (
	statsStructure: any
): ChartData | null => {
	if (!statsStructure) {
		console.log(
			'🔴 transformStatsForChart: statsStructure is null/undefined'
		);
		return null;
	}

	console.log(
		'🔵 transformStatsForChart: Received data:',
		JSON.stringify(statsStructure).substring(0, 200)
	);

	// If already in correct format, return as is
	if (statsStructure.day || statsStructure.month || statsStructure.year) {
		return statsStructure;
	}

	// Check if data is nested in a 'data' property
	if (statsStructure.data && !Array.isArray(statsStructure.data)) {
		return transformStatsForChart(statsStructure.data);
	}

	// If it's an array, transform it to the expected format
	if (Array.isArray(statsStructure)) {
		// Group data by time period
		const transformedData: ChartData = {
			day: { data: [], labels: [] },
			month: { data: [], labels: [] },
			year: { data: [], labels: [] },
		};

		// Assume array items have a date/timestamp and value
		statsStructure.forEach((item: any, index: number) => {
			if (item && typeof item === 'object') {
				// Extract value (could be count, value, or any numeric property)
				const value =
					item.value || item.count || item.y || item.data || 0;

				// Extract label/date
				const label =
					item.label ||
					item.date ||
					item.x ||
					item.name ||
					`Item ${index + 1}`;

				// For simplicity, add to month by default
				// You can enhance this to determine day/month/year based on the date
				transformedData.month.data.push(Number(value));
				transformedData.month.labels.push(String(label));
			}
		});

		// Only return if we have some data
		if (transformedData.month.data.length > 0) {
			return transformedData;
		}

		return null;
	}

	// If it's an object with data/labels at root level, wrap it
	if (statsStructure.data && statsStructure.labels) {
		return {
			month: {
				data: Array.isArray(statsStructure.data)
					? statsStructure.data
					: [],
				labels: Array.isArray(statsStructure.labels)
					? statsStructure.labels
					: [],
			},
			day: { data: [], labels: [] },
			year: { data: [], labels: [] },
		};
	}

	return null;
};

/**
 * Check if chart data has any actual values
 * @param chartData - Chart data to check
 * @returns true if there's data with values > 0
 */
export const hasChartData = (chartData: ChartData | null): boolean => {
	if (!chartData) return false;

	return (
		chartData.month?.data?.length > 0 ||
		chartData.day?.data?.length > 0 ||
		chartData.year?.data?.length > 0
	);
};

/**
 * Get the total count from chart data
 * @param chartData - Chart data
 * @returns Sum of all values
 */
export const getChartDataTotal = (chartData: ChartData | null): number => {
	if (!chartData) return 0;

	let total = 0;

	if (chartData.month?.data) {
		total += chartData.month.data.reduce((sum, val) => sum + val, 0);
	}
	if (chartData.day?.data) {
		total += chartData.day.data.reduce((sum, val) => sum + val, 0);
	}
	if (chartData.year?.data) {
		total += chartData.year.data.reduce((sum, val) => sum + val, 0);
	}

	return total;
};
